import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import {
  LineChart,
  RefreshCw,
  Search,
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  Target,
  Shield,
  Activity,
  ExternalLink,
  Sparkles,
  Calendar,
  AlertCircle,
  Trash2,
  Crown,
  Zap,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";

// Affiliate links
const MC_LINK = "https://marketchameleon.com/?pap_aid=MyTradingJourney";
const TV_LINK = "https://bit.ly/MTJtradingview";

const TIMEFRAMES = [
  { key: "1D", label: "Day (1D)" },
  { key: "1H", label: "Swing (1H)" },
  { key: "15M", label: "Scalp (15M)" },
];

const BIAS_STYLE = {
  LONG: { color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/40", icon: TrendingUp, label: "BULLISH BIAS" },
  SHORT: { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/40", icon: TrendingDown, label: "BEARISH BIAS" },
  NEUTRAL: { color: "text-gray-400", bg: "bg-gray-500/10", border: "border-gray-500/40", icon: Minus, label: "NEUTRAL" },
};

const TREND_LABELS = {
  STRONG_UPTREND: { label: "Strong Uptrend", cls: "text-emerald-400" },
  UPTREND: { label: "Uptrend", cls: "text-emerald-300" },
  NEUTRAL: { label: "Neutral", cls: "text-gray-400" },
  DOWNTREND: { label: "Downtrend", cls: "text-red-300" },
  STRONG_DOWNTREND: { label: "Strong Downtrend", cls: "text-red-400" },
};

function StatCell({ label, value, accent }) {
  return (
    <div className="bg-black/30 rounded p-2 text-center">
      <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
      <p className={`text-sm font-bold ${accent || "text-white"}`}>{value ?? "—"}</p>
    </div>
  );
}

function TradingViewChart({ symbol, timeframe }) {
  const interval = useMemo(() => {
    if (timeframe === "1D") return "D";
    if (timeframe === "1H") return "60";
    if (timeframe === "15M") return "15";
    return "D";
  }, [timeframe]);

  const containerId = `tv-chart-${symbol}-${timeframe}`;
  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;
    container.innerHTML = "";
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/tv.js";
    script.async = true;
    script.onload = () => {
      if (window.TradingView) {
        new window.TradingView.widget({
          symbol: symbol,
          interval: interval,
          theme: "dark",
          style: "1",
          locale: "en",
          toolbar_bg: "#0a0a0a",
          enable_publishing: false,
          allow_symbol_change: true,
          container_id: containerId,
          hide_side_toolbar: false,
          studies: ["RSI@tv-basicstudies", "MACD@tv-basicstudies", "MASimple@tv-basicstudies"],
          autosize: true,
        });
      }
    };
    container.appendChild(script);
    return () => {
      if (container) container.innerHTML = "";
    };
  }, [symbol, interval, containerId]);

  return <div id={containerId} className="w-full h-[500px]" />;
}

export default function SohelAnalysis() {
  const [watchlist, setWatchlist] = useState({ symbols: [], explicit: [] });
  const [selected, setSelected] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [timeframe, setTimeframe] = useState("1D");
  const [addInput, setAddInput] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [topPicks, setTopPicks] = useState({ top_picks: [], scanned_count: 0, qualifying_count: 0, generated_at: null, cache_age_seconds: 0 });
  const [topPicksLoading, setTopPicksLoading] = useState(false);

  const fetchWatchlist = useCallback(async () => {
    try {
      const res = await api.get("/sohel-analysis-watchlist");
      setWatchlist(res.data || { symbols: [], explicit: [] });
    } catch {
      // non-critical
    }
  }, []);

  const fetchTopPicks = useCallback(async (force = false) => {
    setTopPicksLoading(true);
    try {
      const res = await api.get(`/sohel-top-picks${force ? "?refresh=true" : ""}`);
      setTopPicks(res.data || { top_picks: [] });
      if (force) toast.success(`Top Picks refreshed — ${res.data?.qualifying_count || 0} qualifying setups`);
    } catch (e) {
      if (force) toast.error("Could not refresh top picks");
    } finally {
      setTopPicksLoading(false);
    }
  }, []);

  const openAnalysis = useCallback(async (symbol, tf = timeframe, force = false) => {
    setSelected(symbol);
    setAnalysis(null);
    setLoading(true);
    try {
      const res = await api.get(`/sohel-analysis/${symbol}?timeframe=${tf}${force ? "&refresh=true" : ""}`);
      setAnalysis(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to load ${symbol}`);
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  const handleTimeframeChange = (tf) => {
    setTimeframe(tf);
    if (selected) openAnalysis(selected, tf, false);
  };

  const handleRefresh = async () => {
    if (!selected) return;
    setRefreshing(true);
    try {
      await openAnalysis(selected, timeframe, true);
      toast.success("Analysis refreshed");
    } finally {
      setRefreshing(false);
    }
  };

  const handlePaperTradePick = async (pick, e) => {
    e.stopPropagation();
    try {
      await api.post("/paper-portfolio/trades", {
        symbol: pick.symbol,
        side: pick.bias,  // LONG or SHORT
        qty: 100,
        entry_price: pick.current_price,
        stop: pick.stop,
        target: pick.targets?.[0],
        source: "sohel_top_pick",
        notes: `${pick.confidence} conf · MTF ${pick.mtf_alignment || "N/A"} · Δ ${pick.delta}`,
      });
      toast.success(`✓ Paper trade opened: ${pick.symbol} ${pick.bias} — see Paper Portfolio`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open paper trade");
    }
  };

  const handleAdd = async () => {
    const sym = addInput.trim().toUpperCase();
    if (!sym) return;
    setAddBusy(true);
    try {
      await api.post("/sohel-analysis-watchlist/add", { symbol: sym });
      toast.success(`✓ ${sym} added to TA watchlist`);
      setAddInput("");
      fetchWatchlist();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    } finally {
      setAddBusy(false);
    }
  };

  const handleRemove = async (sym, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/sohel-analysis-watchlist/${sym}`);
      toast.success(`${sym} removed`);
      fetchWatchlist();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  useEffect(() => {
    fetchWatchlist();
    fetchTopPicks(false);
  }, [fetchWatchlist, fetchTopPicks]);

  const setup = analysis?.setup;
  const indicators = analysis?.indicators || {};
  const levels = analysis?.levels || {};
  const ctx = analysis?.context || {};
  const biasInfo = setup ? (BIAS_STYLE[setup.bias] || BIAS_STYLE.NEUTRAL) : null;
  const BiasIcon = biasInfo?.icon || Minus;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="sohel-analysis-page">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
            <LineChart className="w-8 h-8 text-cyan-400" />
            SOHEL ANALYSIS
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Premium technical analysis · Polygon-powered S/R, indicators, fib + volume profile · Live TradingView chart
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={MC_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1.5 rounded-sm border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 transition-colors flex items-center gap-1"
            data-testid="mc-affiliate-link"
          >
            MarketChameleon <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href={TV_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1.5 rounded-sm border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 transition-colors flex items-center gap-1"
            data-testid="tv-affiliate-link"
          >
            TradingView <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* 🏆 TOP 10 HIGHEST-CONFIDENCE SETUPS */}
      <Card className="card-dark border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5" data-testid="top-picks-card">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              <h2 className="font-heading font-bold text-lg sm:text-xl tracking-tight text-amber-200">
                TOP 10 PLAYS · TODAY
              </h2>
              <Badge variant="outline" className="text-amber-300 border-amber-500/40 hidden sm:inline-flex">
                <Zap className="w-3 h-3 mr-1" />
                AUTO-RANKED
              </Badge>
            </div>
            <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
              {topPicks.generated_at && (
                <span data-testid="top-picks-stats">
                  {topPicks.qualifying_count}/{topPicks.scanned_count} qualifying · refreshed {Math.round((topPicks.cache_age_seconds || 0) / 60)}m ago
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => fetchTopPicks(true)}
                disabled={topPicksLoading}
                data-testid="top-picks-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${topPicksLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground mb-3">
            Highest-conviction Sohel Analysis setups across your portfolio + watchlists + active Promises.
            Filtered to <span className="text-amber-300 font-bold">HIGH/MEDIUM</span> confidence with multi-timeframe alignment.
            Auto-refreshes every 10 min.
          </p>

          {topPicksLoading && topPicks.top_picks.length === 0 ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-6 h-6 text-amber-400 mx-auto animate-spin mb-2" />
              <p className="text-xs text-muted-foreground">Scanning your universe…</p>
            </div>
          ) : topPicks.top_picks.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center">
              No qualifying setups yet. Add tickers below or wait for next scan.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {topPicks.top_picks.map((p, i) => {
                const isLong = p.bias === "LONG";
                const trendIcon = isLong ? TrendingUp : TrendingDown;
                const TrendIcon = trendIcon;
                const cardClasses = isLong
                  ? "border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 hover:border-emerald-400/60"
                  : "border-red-500/30 bg-red-500/5 hover:bg-red-500/10 hover:border-red-400/60";
                const biasBadgeClasses = isLong
                  ? "bg-emerald-500/15 text-emerald-300 border-0"
                  : "bg-red-500/15 text-red-300 border-0";
                const confColor =
                  p.confidence === "HIGH" ? "text-emerald-300 border-emerald-500/50" :
                  p.confidence === "MEDIUM" ? "text-cyan-300 border-cyan-500/50" :
                  "text-yellow-300 border-yellow-500/50";
                return (
                  <button
                    key={p.symbol}
                    onClick={() => openAnalysis(p.symbol)}
                    data-testid={`top-pick-${p.symbol}`}
                    className={`group relative text-left p-2.5 rounded-sm border ${cardClasses} transition-all`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        {i + 1}
                      </span>
                      <span className="font-heading font-bold text-base text-white">{p.symbol}</span>
                      <span className="text-xs font-data text-muted-foreground">${p.current_price}</span>
                      <Badge className={`ml-auto text-[10px] py-0 ${biasBadgeClasses}`}>
                        <TrendIcon className="w-3 h-3 mr-0.5" />
                        {p.bias}
                      </Badge>
                      <Badge variant="outline" className={`${confColor} text-[10px] py-0`}>
                        {p.confidence}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-1 text-[10px]">
                      <div>
                        <p className="text-muted-foreground uppercase">Entry</p>
                        <p className="text-cyan-300 font-data">${p.entry_zone?.low}-${p.entry_zone?.high}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground uppercase">Stop</p>
                        <p className="text-red-300 font-data">${p.stop}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground uppercase">T1</p>
                        <p className="text-emerald-300 font-data">${p.targets?.[0] || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground uppercase">R/R</p>
                        <p className="text-yellow-300 font-data font-bold">{p.risk_reward}x</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mt-1.5 text-[9px]">
                      {p.mtf_alignment && (
                        <span className={`px-1 py-0.5 rounded ${
                          p.mtf_alignment === "BULLISH_ALIGNED" ? "bg-emerald-500/15 text-emerald-300" :
                          p.mtf_alignment === "BEARISH_ALIGNED" ? "bg-red-500/15 text-red-300" :
                          "bg-amber-500/15 text-amber-300"
                        }`}>
                          MTF {p.mtf_alignment.replace("_ALIGNED", "")}
                        </span>
                      )}
                      <span className="text-muted-foreground">Δ {p.delta}</span>
                      {p.in_portfolio && <span className="text-pink-300">💼 Portfolio</span>}
                      {p.in_watchlist && <span className="text-yellow-300">⭐</span>}
                      {p.in_emergent_promise && <span className="text-purple-300">💎 Promise</span>}
                      {p.next_earnings_days !== null && p.next_earnings_days !== undefined && p.next_earnings_days <= 7 && (
                        <span className="text-amber-300">📅 ER in {p.next_earnings_days}d</span>
                      )}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handlePaperTradePick(p, e)}
                        onKeyDown={(e) => { if (e.key === "Enter") handlePaperTradePick(p, e); }}
                        data-testid={`paper-trade-${p.symbol}`}
                        className="ml-auto px-1.5 py-0.5 rounded border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 transition-colors"
                      >
                        + Paper
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Watchlist + add input */}
      <Card className="card-dark border-cyan-500/20 bg-cyan-500/5">
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Input
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              placeholder="Add ticker (e.g., NVDA)"
              className="bg-background/50 max-w-xs"
              data-testid="sa-watchlist-input"
            />
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={addBusy || !addInput.trim()}
              data-testid="sa-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
            <span className="text-xs text-muted-foreground ml-auto">
              {watchlist.symbols.length} tickers · auto-includes your portfolio + Sohel Pick watchlist
            </span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="sa-watchlist-grid">
            {watchlist.symbols.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                No tickers yet. Add one above or any holdings will auto-populate here.
              </p>
            ) : (
              watchlist.symbols.map((sym) => {
                const isExplicit = watchlist.explicit.includes(sym);
                return (
                  <button
                    key={sym}
                    onClick={() => openAnalysis(sym)}
                    data-testid={`sa-chip-${sym}`}
                    className="group flex items-center gap-1 px-3 py-1.5 rounded-sm border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 hover:border-cyan-400/60 transition-colors"
                  >
                    <span className="text-sm font-bold text-white">{sym}</span>
                    {isExplicit && (
                      <Trash2
                        onClick={(e) => handleRemove(sym, e)}
                        className="w-3 h-3 text-gray-500 hover:text-red-400 ml-1"
                        data-testid={`sa-remove-${sym}`}
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setAnalysis(null); } }}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto bg-background border-cyan-500/30">
          {!analysis && loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-cyan-400 mx-auto animate-spin mb-2" />
              <p className="text-sm text-muted-foreground">Analyzing {selected}…</p>
            </div>
          ) : analysis ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 pb-3 border-b border-border">
                <div>
                  <h2 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
                    {analysis.symbol}
                    <span className="text-2xl font-data text-cyan-400">${analysis.current_price}</span>
                  </h2>
                  <div className="flex items-center gap-2 text-xs mt-1">
                    <Badge variant="outline" className={TREND_LABELS[analysis.trend]?.cls || ""}>
                      {TREND_LABELS[analysis.trend]?.label || analysis.trend}
                    </Badge>
                    {biasInfo && (
                      <Badge className={`${biasInfo.bg} ${biasInfo.color} border-0`}>
                        <BiasIcon className="w-3 h-3 mr-1" />
                        {biasInfo.label}
                      </Badge>
                    )}
                    {ctx.in_portfolio && <Badge variant="outline" className="text-pink-400 border-pink-500/40">💼 In Portfolio</Badge>}
                    {ctx.in_watchlist && <Badge variant="outline" className="text-yellow-400 border-yellow-500/40">⭐ Starred</Badge>}
                    {ctx.in_emergent_promise && <Badge variant="outline" className="text-purple-400 border-purple-500/40">💎 Active Promise</Badge>}
                    {ctx.next_earnings_days !== null && ctx.next_earnings_days !== undefined && (
                      <Badge variant="outline" className="text-amber-400 border-amber-500/40">
                        <Calendar className="w-3 h-3 mr-1" />
                        Earnings in {ctx.next_earnings_days}d
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <Tabs value={timeframe} onValueChange={handleTimeframeChange}>
                    <TabsList className="bg-background/50">
                      {TIMEFRAMES.map((tf) => (
                        <TabsTrigger key={tf.key} value={tf.key} data-testid={`sa-tf-${tf.key}`}>
                          {tf.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    data-testid="sa-refresh-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </div>

              {/* TradingView chart */}
              <div className="rounded-sm overflow-hidden border border-border">
                <TradingViewChart symbol={analysis.symbol} timeframe={timeframe} />
              </div>

              {/* Trade Setup card */}
              {setup && (
                <Card className={`card-dark ${biasInfo.border} ${biasInfo.bg}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className={`w-5 h-5 ${biasInfo.color}`} />
                      <h3 className="font-heading font-bold text-lg tracking-tight">SOHEL TRADE SETUP</h3>
                      <Badge className={`${biasInfo.bg} ${biasInfo.color} border-0 ml-auto`}>
                        {setup.bias}
                      </Badge>
                      {setup.confidence && setup.confidence !== "NONE" && (
                        <Badge
                          variant="outline"
                          className={
                            setup.confidence === "HIGH" ? "text-emerald-300 border-emerald-500/50" :
                            setup.confidence === "MEDIUM" ? "text-cyan-300 border-cyan-500/50" :
                            "text-yellow-300 border-yellow-500/50"
                          }
                          data-testid="sa-confidence-badge"
                        >
                          {setup.confidence} CONF
                        </Badge>
                      )}
                      {analysis.mtf_alignment?.alignment && (
                        <Badge
                          variant="outline"
                          className={
                            analysis.mtf_alignment.alignment === "BULLISH_ALIGNED" ? "text-emerald-300 border-emerald-500/50" :
                            analysis.mtf_alignment.alignment === "BEARISH_ALIGNED" ? "text-red-300 border-red-500/50" :
                            analysis.mtf_alignment.alignment === "CONFLICTED" ? "text-amber-300 border-amber-500/50" :
                            "text-gray-400 border-gray-500/50"
                          }
                          data-testid="sa-mtf-badge"
                        >
                          MTF: {analysis.mtf_alignment.alignment.replace("_", " ")}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <StatCell
                        label="Entry Zone"
                        value={setup.entry_zone ? `$${setup.entry_zone.low} – $${setup.entry_zone.high}` : "Wait"}
                        accent="text-cyan-400"
                      />
                      <StatCell
                        label="Stop"
                        value={setup.stop ? `$${setup.stop}` : "—"}
                        accent="text-red-400"
                      />
                      <StatCell
                        label="Target 1"
                        value={setup.targets?.[0] ? `$${setup.targets[0]}` : "—"}
                        accent="text-emerald-400"
                      />
                      <StatCell
                        label="Target 2 / 3"
                        value={setup.targets?.[1] ? `$${setup.targets[1]}${setup.targets[2] ? ` / $${setup.targets[2]}` : ""}` : "—"}
                        accent="text-emerald-300"
                      />
                      <StatCell
                        label="R/R"
                        value={setup.risk_reward ? `${setup.risk_reward}x` : "—"}
                        accent="text-yellow-400"
                      />
                    </div>
                    {analysis.bias_score && (
                      <div className="mt-3 p-2 bg-black/40 rounded text-[11px]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-muted-foreground uppercase">Confluence Score</span>
                          <span className="font-data">
                            <span className="text-emerald-400">Bull {analysis.bias_score.bull_score}</span>
                            {" / "}
                            <span className="text-red-400">Bear {analysis.bias_score.bear_score}</span>
                            <span className="ml-2 text-cyan-300">Δ {analysis.bias_score.delta}</span>
                          </span>
                        </div>
                        {Array.isArray(analysis.bias_score.factors) && analysis.bias_score.factors.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {analysis.bias_score.factors
                              .filter(f => f.weight > 0)
                              .sort((a, b) => b.weight - a.weight)
                              .slice(0, 8)
                              .map((f, i) => (
                                <span
                                  key={i}
                                  title={f.note}
                                  className={`px-1.5 py-0.5 rounded text-[10px] border ${
                                    f.side === "bull" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" :
                                    f.side === "bear" ? "border-red-500/40 text-red-300 bg-red-500/5" :
                                    "border-gray-500/40 text-gray-400"
                                  }`}
                                >
                                  {f.factor} +{f.weight}
                                </span>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                    {Array.isArray(setup.rationale) && setup.rationale.length > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-2 text-center italic">
                        {setup.rationale.join(" · ")}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Indicators grid */}
              <Card className="card-dark border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    <h3 className="font-heading font-bold text-lg tracking-tight">INDICATORS</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <StatCell
                      label="RSI(14)"
                      value={indicators.rsi_14 ? indicators.rsi_14.toFixed(1) : "—"}
                      accent={
                        indicators.rsi_14 > 70 ? "text-red-400" :
                        indicators.rsi_14 < 30 ? "text-emerald-400" : "text-white"
                      }
                    />
                    <StatCell
                      label="MACD"
                      value={indicators.macd?.cross || "—"}
                      accent={
                        indicators.macd?.cross?.includes("BULL") ? "text-emerald-400" :
                        indicators.macd?.cross?.includes("BEAR") ? "text-red-400" : "text-white"
                      }
                    />
                    <StatCell label="SMA 20" value={indicators.sma_20 ? `$${indicators.sma_20}` : "—"} />
                    <StatCell label="SMA 50" value={indicators.sma_50 ? `$${indicators.sma_50}` : "—"} />
                    <StatCell label="SMA 200" value={indicators.sma_200 ? `$${indicators.sma_200}` : "—"} />
                    <StatCell label="ATR(14)" value={indicators.atr_14 ? `$${indicators.atr_14}` : "—"} />
                    <StatCell label="VWAP(20)" value={indicators.vwap_20 ? `$${indicators.vwap_20}` : "—"} />
                    <StatCell
                      label="OBV Trend"
                      value={indicators.obv?.trend || "—"}
                      accent={indicators.obv?.trend === "RISING" ? "text-emerald-400" : "text-red-400"}
                    />
                    <StatCell
                      label="ADX(14)"
                      value={indicators.adx?.adx ? `${indicators.adx.adx}${indicators.adx.adx > 25 ? " ●" : ""}` : "—"}
                      accent={
                        indicators.adx?.adx > 25 ? "text-cyan-400" :
                        indicators.adx?.adx < 20 ? "text-gray-400" : "text-white"
                      }
                    />
                    <StatCell
                      label="Stoch RSI"
                      value={indicators.stoch_rsi?.k ? `K ${indicators.stoch_rsi.k} / D ${indicators.stoch_rsi.d}` : "—"}
                      accent={
                        indicators.stoch_rsi?.k > 80 ? "text-red-400" :
                        indicators.stoch_rsi?.k < 20 ? "text-emerald-400" : "text-white"
                      }
                    />
                    <StatCell
                      label="Vol Ratio"
                      value={indicators.volume_ratio ? `${indicators.volume_ratio}x` : "—"}
                      accent={indicators.volume_ratio > 1.5 ? "text-amber-400" : "text-white"}
                    />
                  </div>
                  {indicators.bollinger && (
                    <div className="mt-3 p-2 bg-black/30 rounded">
                      <p className="text-[10px] text-muted-foreground uppercase mb-1">Bollinger Bands (20, 2σ)</p>
                      <p className="text-xs text-white">
                        Lower: <span className="text-red-400">${indicators.bollinger.lower}</span> · Mid: ${indicators.bollinger.mid} ·
                        Upper: <span className="text-emerald-400">${indicators.bollinger.upper}</span>
                        <span className="ml-3 text-cyan-400">Position: {(indicators.bollinger.position * 100).toFixed(0)}% of band</span>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Key levels */}
              <Card className="card-dark border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-purple-400" />
                    <h3 className="font-heading font-bold text-lg tracking-tight">SUPPORT &amp; RESISTANCE</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Resistance */}
                    <div className="space-y-1">
                      <p className="text-[10px] text-emerald-400 uppercase font-bold">RESISTANCE (next ceilings)</p>
                      {(levels?.confluence_resistances || []).slice(0, 5).map((lvl, i) => {
                        const dist = ((lvl.price - analysis.current_price) / analysis.current_price) * 100;
                        return (
                          <div key={i} className="flex items-center gap-2 px-2 py-1 bg-emerald-500/5 border-l-2 border-emerald-500/40 text-xs">
                            <span className="text-emerald-300 font-bold w-12">R{i + 1}</span>
                            <span className="text-white font-data">${lvl.price}</span>
                            <span className="text-[9px] text-emerald-400/70">●{lvl.strength}</span>
                            <span className="ml-auto text-muted-foreground">+{dist.toFixed(1)}%</span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Support */}
                    <div className="space-y-1">
                      <p className="text-[10px] text-red-400 uppercase font-bold">SUPPORT (next floors)</p>
                      {(levels?.confluence_supports || []).slice(0, 5).map((lvl, i) => {
                        const dist = ((analysis.current_price - lvl.price) / analysis.current_price) * 100;
                        return (
                          <div key={i} className="flex items-center gap-2 px-2 py-1 bg-red-500/5 border-l-2 border-red-500/40 text-xs">
                            <span className="text-red-300 font-bold w-12">S{i + 1}</span>
                            <span className="text-white font-data">${lvl.price}</span>
                            <span className="text-[9px] text-red-400/70">●{lvl.strength}</span>
                            <span className="ml-auto text-muted-foreground">−{dist.toFixed(1)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Pivot, Fib, VolProfile mini */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-xs">
                    {levels.pivot_points && (
                      <div className="p-2 bg-black/30 rounded">
                        <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">Pivot Points</p>
                        <p className="text-white">Pivot ${levels.pivot_points.pivot}</p>
                        <p className="text-emerald-400">R1 ${levels.pivot_points.r1} · R2 ${levels.pivot_points.r2} · R3 ${levels.pivot_points.r3}</p>
                        <p className="text-red-400">S1 ${levels.pivot_points.s1} · S2 ${levels.pivot_points.s2} · S3 ${levels.pivot_points.s3}</p>
                      </div>
                    )}
                    {levels.fibonacci && Object.keys(levels.fibonacci).length > 0 && (
                      <div className="p-2 bg-black/30 rounded">
                        <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">
                          Fibonacci ({levels.fibonacci.trend})
                        </p>
                        <p className="text-cyan-400">23.6% ${levels.fibonacci.fib_236}</p>
                        <p className="text-cyan-400">38.2% ${levels.fibonacci.fib_382}</p>
                        <p className="text-yellow-400">50.0% ${levels.fibonacci.fib_500}</p>
                        <p className="text-yellow-400">61.8% ${levels.fibonacci.fib_618}</p>
                        <p className="text-pink-400">78.6% ${levels.fibonacci.fib_786}</p>
                      </div>
                    )}
                    {levels.volume_profile && Object.keys(levels.volume_profile).length > 0 && (
                      <div className="p-2 bg-black/30 rounded">
                        <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">Volume Profile</p>
                        <p className="text-purple-400 font-bold">POC ${levels.volume_profile.poc}</p>
                        <p className="text-emerald-300">VAH ${levels.volume_profile.value_area_high}</p>
                        <p className="text-red-300">VAL ${levels.volume_profile.value_area_low}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Cross-module context */}
              {(ctx.earnings || ctx.whale_flow || ctx.recent_news?.length > 0) && (
                <Card className="card-dark border-yellow-500/20 bg-yellow-500/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-yellow-400" />
                      <h3 className="font-heading font-bold text-lg tracking-tight">CROSS-MODULE INTEL</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      {ctx.earnings && (
                        <div className="p-2 bg-black/30 rounded">
                          <p className="text-[10px] text-amber-400 font-bold uppercase">Earnings AI</p>
                          <p className="text-white">{ctx.earnings.confidence}% conf · {ctx.earnings.direction}</p>
                          <p className="text-muted-foreground">In {ctx.earnings.days_until}d · expected {ctx.earnings.expected_move_pct}% move</p>
                        </div>
                      )}
                      {ctx.whale_flow && (
                        <div className="p-2 bg-black/30 rounded">
                          <p className="text-[10px] text-cyan-400 font-bold uppercase">🐋 Whale Flow</p>
                          <p className="text-white">{ctx.whale_flow.action} · {ctx.whale_flow.confidence}% conf</p>
                          {ctx.whale_flow.top_whale_trade && (
                            <p className="text-muted-foreground">{ctx.whale_flow.top_whale_trade}</p>
                          )}
                        </div>
                      )}
                      {ctx.recent_news?.length > 0 && (
                        <div className="p-2 bg-black/30 rounded">
                          <p className="text-[10px] text-blue-400 font-bold uppercase">📰 Recent News (24h)</p>
                          {ctx.recent_news.slice(0, 3).map((n, i) => (
                            <a key={i} href={n.url} target="_blank" rel="noopener noreferrer" className="block text-white hover:text-cyan-300 line-clamp-1 leading-snug">
                              {n.ai_sentiment === "BULLISH" ? "🟢" : n.ai_sentiment === "BEARISH" ? "🔴" : "⚪"} {n.title}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Affiliate footer */}
              <div className="flex items-center gap-2 pt-2">
                <a
                  href={`${MC_LINK}&symbol=${analysis.symbol}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-3 py-2 rounded-sm border border-purple-500/40 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 transition-colors flex items-center gap-1"
                >
                  Open {analysis.symbol} on MarketChameleon <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href={`https://www.tradingview.com/symbols/${analysis.symbol}/?aff_id=MTJtradingview`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-3 py-2 rounded-sm border border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 transition-colors flex items-center gap-1"
                >
                  Open {analysis.symbol} on TradingView <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <Card className="card-dark border-yellow-500/20 bg-yellow-500/5">
                <CardContent className="p-2 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-yellow-200/80 leading-relaxed">
                    Algorithmic technical setup — not financial advice. Past patterns ≠ future. Always combine with fundamentals, news, and your own discipline.
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
